/**
 * The Agapaé AI side panel — right drawer per the app's drawer pattern, as
 * in GuestDrawer (width via slotProps.paper — NEVER PaperProps, the aurora theme
 * ignores it): sticky header / scrollable body / sticky composer footer.
 *
 * Body layout: empty conversation → AssistantHome (planning status +
 * dynamic chips — the product leads with "here's where you are", chat
 * complements it); once messages exist → the streaming thread; the ⋯ menu's
 * "Chat history" swaps the body to the past-conversations list (the only
 * history surface on mobile, where the planner page redirects away).
 *
 * Expanding to the full-screen planner CONTINUES the same conversation —
 * with messages on screen a small chooser offers "continue" vs "start new"
 * (non-destructive either way: the old thread lands in history).
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'providers/AuthProvider';
import { useBreakpoints } from 'providers/BreakpointsProvider';
import { isProPlan } from 'utils/plan';
import { weddingPaths } from 'routes/paths';
import IconifyIcon from 'components/base/IconifyIcon';
import { translateApiMessage } from 'utils/translateApiMessage';
import { isBudgetExhaustedError } from 'services/api/assistant';
import AssistantComposer from './AssistantComposer';
import AssistantConversation from './AssistantConversation';
import AssistantDisclaimer from './AssistantDisclaimer';
import AssistantExhaustedNotice from './AssistantExhaustedNotice';
import AssistantHistoryList from './AssistantHistoryList';
import AssistantHome from './AssistantHome';
import AssistantOverflowMenu from './AssistantOverflowMenu';
import AssistantUpsell from './AssistantUpsell';
import AssistantUsageHint from './AssistantUsageHint';
import { useAssistantChat } from './useAssistantChat';
import { useAssistantConversations } from './useAssistantConversations';

interface AssistantDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Pending message that auto-sends once the chat is hydrated and idle. */
  seed?: string | null;
  /** Called the moment the seed is handed to the chat (parent clears its state). */
  onSeedConsumed?: () => void;
}

const AssistantDrawer = ({ open, onClose, seed, onSeedConsumed }: AssistantDrawerProps) => {
  const { t } = useTranslation();
  const { sessionUser } = useAuth();
  const { down } = useBreakpoints();
  const navigate = useNavigate();
  const isMobile = down('md');
  const eventId = sessionUser?.eventId;
  // The companion is Heirloom-only. A non-pro couple gets the upsell body
  // instead of the chat — and we never call the API for them (it would 403).
  // Safe to gate on plan here: only couples reach the drawer (launcher is
  // role-gated) and the BE enforces pro regardless.
  const isPro = isProPlan(sessionUser?.plan);

  const {
    messages,
    streamingText,
    toolActivity,
    status,
    error,
    restoredDraft,
    send,
    reset,
    activate,
    clearError,
    retry,
  } = useAssistantChat({
    eventId,
    enabled: open && isPro,
  });

  const isStreaming = status === 'streaming';

  // In-drawer history view (the ⋯ menu's "Chat history") — swaps the body to
  // the past-conversations list. The list fetches only while the view is open.
  const [historyOpen, setHistoryOpen] = useState(false);
  const {
    conversations,
    loading: conversationsLoading,
    error: conversationsError,
  } = useAssistantConversations({
    eventId,
    enabled: open && isPro && historyOpen && !!eventId,
  });

  const showHome = eventId && !historyOpen && messages.length === 0 && status !== 'loading';

  // Expand chooser — with messages on screen the user picks "continue" vs
  // "start new"; an empty conversation just navigates (nothing to choose).
  const expandRef = useRef<HTMLButtonElement>(null);
  const [expandMenuOpen, setExpandMenuOpen] = useState(false);
  const handleExpandClick = () => {
    if (messages.length === 0) {
      navigate(weddingPaths.couplePlanner);
    } else {
      setExpandMenuOpen(true);
    }
  };
  const handleExpandContinue = () => {
    setExpandMenuOpen(false);
    navigate(weddingPaths.couplePlanner);
  };
  const handleExpandNew = async () => {
    setExpandMenuOpen(false);
    // Non-destructive: the current thread is archived into history first.
    await reset();
    navigate(weddingPaths.couplePlanner);
  };

  const handleNewChat = () => {
    // "New chat" means "go compose" — leave the history view if it's open.
    setHistoryOpen(false);
    void reset();
  };
  const handleHistorySelect = (conversationId: string) => {
    // Only leave the history view once the swap actually succeeds — a streaming
    // lock (409) or error keeps the list open with the notice, instead of
    // bouncing back to the unchanged thread as if nothing failed (UX-6).
    void activate(conversationId).then((ok) => {
      if (ok) setHistoryOpen(false);
    });
  };

  // Auto-send a pending seed (e.g. the dashboard's invitation-setup nudge) as a
  // normal visible user message — same semantics as tapping a suggestion chip.
  // Waits for 'idle' so it lands AFTER hydration and after any reply that was
  // still streaming when the drawer reopened (the component stays mounted while
  // closed). The ref guards double-fires (StrictMode / re-renders) until the
  // parent's onSeedConsumed state update commits; it re-arms on close. Non-pro
  // sees the upsell instead — the seed is silently dropped (cleared on close).
  const seedSentRef = useRef(false);
  const seedRetryRef = useRef(false);
  useEffect(() => {
    if (!open) {
      seedSentRef.current = false;
      seedRetryRef.current = false;
      // Reopening always lands on the thread, not a stale history view.
      setHistoryOpen(false);
      return;
    }
    if (!seed || !isPro || !eventId) return;
    // Hydration failed before the seeded message could send — retry the load once
    // so the "finish your invitation" nudge doesn't strand a dead drawer (UX-5).
    if (status === 'error' && !seedSentRef.current && !seedRetryRef.current) {
      seedRetryRef.current = true;
      retry();
      return;
    }
    if (status !== 'idle') return;
    if (seedSentRef.current) return;
    seedSentRef.current = true;
    onSeedConsumed?.();
    // A seed must land in the VISIBLE thread — never behind the history view.
    setHistoryOpen(false);
    send(seed);
  }, [open, seed, isPro, eventId, status, send, onSeedConsumed, retry]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: isMobile ? '100%' : 600,
            maxWidth: '100vw',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {/* Sticky header */}
      <Box sx={{ p: { xs: 2.5, md: 3 }, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          {historyOpen ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 2 }}>
              <IconButton
                onClick={() => setHistoryOpen(false)}
                size="small"
                aria-label={t('assistant.history.back_aria')}
              >
                <IconifyIcon
                  icon="material-symbols:arrow-back-rounded"
                  sx={{ fontSize: 22, color: 'neutral.dark' }}
                />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t('assistant.history.title')}
              </Typography>
            </Stack>
          ) : (
            <Stack direction="column" spacing={0.5} sx={{ pr: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconifyIcon
                  icon="material-symbols:auto-awesome"
                  sx={{ fontSize: 20, color: 'primary.main' }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t('assistant.product_name')}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('assistant.drawer.subtitle')}
              </Typography>
            </Stack>
          )}
          <Stack direction="row" spacing={0.5} alignItems="center">
            {/* Expand to the full-screen planner — desktop only (the page
                redirects on mobile, where the drawer stays the experience). */}
            {isPro && eventId && !isMobile && (
              <>
                <Tooltip title={t('assistant.drawer.expand')}>
                  <IconButton
                    ref={expandRef}
                    onClick={handleExpandClick}
                    size="small"
                    aria-label={t('assistant.drawer.expand')}
                  >
                    <IconifyIcon
                      icon="material-symbols:open-in-full-rounded"
                      sx={{ fontSize: 20, color: 'neutral.dark' }}
                    />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={expandRef.current}
                  open={expandMenuOpen}
                  onClose={() => setExpandMenuOpen(false)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem onClick={handleExpandContinue}>
                    <ListItemIcon>
                      <IconifyIcon icon="material-symbols:open-in-full-rounded" />
                    </ListItemIcon>
                    <ListItemText>{t('assistant.drawer.expand_continue')}</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleExpandNew} disabled={isStreaming}>
                    <ListItemIcon>
                      <IconifyIcon icon="material-symbols:edit-square-outline-rounded" />
                    </ListItemIcon>
                    <ListItemText>{t('assistant.drawer.expand_new')}</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )}
            {isPro && eventId && (
              <AssistantOverflowMenu
                eventId={eventId}
                onNewChat={handleNewChat}
                newChatDisabled={isStreaming}
                onOpenHistory={() => setHistoryOpen(true)}
              />
            )}
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ minWidth: 44, minHeight: 44 }}
              aria-label={t('assistant.drawer.close_aria')}
            >
              <IconifyIcon
                icon="material-symbols:close"
                sx={{ fontSize: 22, color: 'neutral.dark' }}
              />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {!isPro ? (
          <AssistantUpsell />
        ) : historyOpen ? (
          <Box sx={{ px: 1.5, py: 1.5 }}>
            <AssistantHistoryList
              conversations={conversations}
              loading={conversationsLoading}
              error={conversationsError}
              disabled={isStreaming}
              onSelect={handleHistorySelect}
            />
          </Box>
        ) : (
          <>
            {showHome && <AssistantHome eventId={eventId} onPrompt={send} disabled={isStreaming} />}
            {eventId && messages.length > 0 && (
              <AssistantConversation
                messages={messages}
                streamingText={streamingText}
                isStreaming={isStreaming}
                toolActivity={toolActivity}
              />
            )}
          </>
        )}
      </Box>

      {/* Error notice + sticky composer footer (pro only — the upsell body
          carries its own CTAs and needs no composer; hidden while browsing
          history). */}
      {isPro && !historyOpen && (
        <Box
          sx={{
            px: { xs: 2, md: 2.5 },
            py: { xs: 1.5, md: 2 },
            borderTop: 1,
            borderColor: 'divider',
            flexShrink: 0,
            bgcolor: 'background.paper',
          }}
        >
          <Stack direction="column" spacing={1.5}>
            {eventId && <AssistantUsageHint eventId={eventId} refreshKey={messages.length} />}
            {isBudgetExhaustedError(error) ? (
              <AssistantExhaustedNotice />
            ) : (
              Boolean(error) && (
                <Alert
                  severity="warning"
                  variant="standard"
                  role="alert"
                  onClose={clearError}
                  sx={{ py: 0.5 }}
                >
                  {translateApiMessage(error, t, {
                    fallback: t('assistant.errors.unavailable'),
                  })}
                </Alert>
              )
            )}
            <AssistantComposer
              disabled={isStreaming || !eventId || isBudgetExhaustedError(error)}
              onSend={send}
              restoredDraft={isBudgetExhaustedError(error) ? null : restoredDraft}
            />
            <AssistantDisclaimer />
          </Stack>
        </Box>
      )}
    </Drawer>
  );
};

export default AssistantDrawer;
